// src/config/navigation.ts
// Single source of truth for the primary navigation destinations, shared by the
// sidebar and the command palette so they can never drift apart.
//
// NOTE: `module` values map to permission IDs stored in the database and MUST
// NOT be renamed. Group display names and submenu labels are safe to change.

import {
  HomeIcon,
  UsersIcon,
  GridIcon,
  FileTextIcon,
  BarChartIcon,
  SettingsIcon,
  BookOpenIcon,
  ClipboardListIcon,
} from 'lucide-react';
import { NavigationModule } from '../types/modulePermissions';

export interface NavSubItem {
  name: string;
  path: string;
  section?: string; // Optional heading rendered above this item, grouping the submenu (e.g. "Advanced").
}

export interface NavGroup {
  name: string;
  path?: string;
  icon: React.ComponentType<{ className?: string }>;
  module: NavigationModule;
  submenu?: NavSubItem[];
}

// Ordered to mirror how the year actually flows: enter your staff → gather their
// intentions → timetable & allocate them → (curriculum assignments) → release load
// letters → look at the reports, then settings.
export const NAV_GROUPS: NavGroup[] = [
  { name: 'Dashboard', path: '/dashboard', icon: HomeIcon, module: 'dashboard' },
  {
    name: 'Staff',
    icon: UsersIcon,
    module: 'staff_management',
    submenu: [
      { name: 'Staff Directory', path: '/staff/directory' },
      { name: 'Allowances', path: '/staff/allowances' },
      { name: 'Load Summaries', path: '/staff/load-summaries' },
    ],
  },
  {
    name: 'Intentions',
    icon: ClipboardListIcon,
    module: 'staff_management',
    submenu: [
      // Personal submission form — always listed so staff can always find it;
      // when no period is open the page itself explains that it's closed.
      { name: 'Submit my intentions', path: '/intentions/submit' },
      { name: 'Teaching Intentions', path: '/intentions' },
      { name: 'Review Intentions', path: '/hr/intention-review' },
    ],
  },
  {
    name: 'Timetable & Allocation',
    icon: GridIcon,
    module: 'class_allocation',
    submenu: [
      // Everyday work — the pages a coordinator uses most.
      { name: 'Build a Grid', path: '/grid/setup' },
      { name: 'Timetable Editor', path: '/grid/multi-editor' },
      { name: 'Class List', path: '/grid/class-management' },
      { name: 'Conflicts', path: '/grid/conflicts' },
      // Advanced / occasional — one-time setup and bulk/history tools, grouped
      // under a heading so they don't clutter the everyday list.
      { name: 'Import Grid', path: '/grid/import', section: 'Advanced' },
      { name: 'Subject Availability', path: '/grid/subject-config', section: 'Advanced' },
      { name: 'Bulk Assign', path: '/allocation/bulk', section: 'Advanced' },
      { name: 'Assignment History', path: '/allocation/history', section: 'Advanced' },
      // Removed (audit 2026-08-13, phase 2): "Publish to Curriculum" (wrote a
      // table nothing reads), "AI Suggestions" (AI was an unimplemented stub),
      // and "Line Times" (saved times nothing consumed) — pages/routes deleted.
    ],
  },
  {
    name: 'Curriculum',
    icon: BookOpenIcon,
    module: 'curriculum_areas',
    submenu: [
      { name: 'Teacher Assignments', path: '/curriculum/assignments' },
      { name: 'Grid Assignments', path: '/curriculum/grids' },
    ],
  },
  {
    name: 'Load Letters',
    icon: FileTextIcon,
    module: 'load_letter_system',
    submenu: [
      { name: 'Generate Letters', path: '/load-letter/generation' },
      { name: 'Letter Template', path: '/load-letter/template-settings' },
      { name: 'My Load Letter', path: '/load-letter/staff-dashboard' },
    ],
  },
  {
    name: 'Reports',
    icon: BarChartIcon,
    module: 'reports_analytics',
    submenu: [
      { name: 'All Reports', path: '/reports' },
      { name: 'Preference Satisfaction', path: '/reports/preference-satisfaction' },
      { name: 'Preference Match', path: '/reports/preference-match' },
      { name: 'Workload Equity', path: '/reports/workload-equity' },
      { name: 'Staff FTE', path: '/reports/staff-fte' },
      { name: 'FTE & Days Off', path: '/reports/fte-days-off' },
      { name: 'Staff by Teaching', path: '/reports/staff-by-teaching' },
      { name: 'Teaching Intentions', path: '/reports/teaching-intentions' },
      { name: 'Recruitment Gaps', path: '/reports/recruitment-gaps' },
      { name: 'RUAH Term Comparison', path: '/reports/ruah-term-comparison' },
      { name: 'Timetabled Subs', path: '/reports/timetabled-subs' },
    ],
  },
  {
    name: 'Settings',
    icon: SettingsIcon,
    module: 'system_settings',
    submenu: [
      { name: 'School Settings', path: '/admin/school-settings' },
      { name: 'Intention Periods', path: '/admin/intention-periods' },
      { name: 'Allowance Templates', path: '/settings/allowance-templates' },
      { name: 'Grid Types', path: '/settings/grid-types' },
      { name: 'Users', path: '/settings/user-management' },
      { name: 'Roles & Permissions', path: '/settings/role-management' },
      { name: 'AI Configuration', path: '/settings/openai-configuration' },
      { name: 'Import Staff Data', path: '/staff/import-data' },
    ],
  },
];

export interface FlatDestination {
  name: string;
  path: string;
  group: string;
  module: NavigationModule;
  icon: React.ComponentType<{ className?: string }>;
}

/** Flatten NAV_GROUPS into individual navigable destinations (for search/palette). */
export function flattenDestinations(): FlatDestination[] {
  const out: FlatDestination[] = [];
  for (const group of NAV_GROUPS) {
    if (group.path) {
      out.push({ name: group.name, path: group.path, group: group.name, module: group.module, icon: group.icon });
    }
    for (const item of group.submenu ?? []) {
      out.push({ name: item.name, path: item.path, group: group.name, module: group.module, icon: group.icon });
    }
  }
  return out;
}
